<script setup lang="ts">
/**
 * SignaturePad (Phase 12, frozen contract for Phase 13; UI-SPEC "Shared
 * Contract: SignaturePad").
 *
 * Thin wrapper over `signature_pad`. The library instance is held in a plain
 * non-reactive `let` (never a ref). Ink is black on a transparent background
 * and exported as PNG only (D-04); `dark:invert` on the canvas is display-only
 * CSS and never touches the exported pixels. The backing store is sized from
 * the canvas CSS box times the device pixel ratio clamped to at most 2.
 *
 * Resize (PAD-04): the canvas re-sizes only when its own CSS box or the
 * clamped ratio changes, so keyboard and URL-bar viewport changes never clear
 * strokes. A box change snapshots the strokes and replays them, scaled down
 * to fit when the box shrank. A resize that lands mid-stroke is deferred to
 * the end of the stroke, because the library's clear() would break it.
 * Pointer capture on pointerdown keeps a stroke released outside the panel
 * on the canvas (PAD-05).
 *
 * Bundle (RESEARCH Pitfall 11): the `@sera/ui` barrel puts this component in
 * the entry chunk, so the library itself loads with a dynamic import in
 * `onMounted`; `scripts/audit-bundle.mjs` fails the build if its runtime
 * reaches the entry graph. Every method tolerates the instance not existing
 * yet (empty, not ink-ok, no data URL).
 *
 * Privacy (D-19): the root carries the Datadog privacy attribute (hidden) and
 * `data-sentry-block`. The drawn data URL is only ever RETURNED from
 * `toPngDataUrl()` to the caller; this component never logs, stores, or emits
 * it.
 *
 * Ink sufficiency (PAD-03): `inkOk()` and every `update:inkState` emit come
 * from `assessInk(pad.toData(), box)` recomputed at call time, never from a
 * cached flag. The hint line under the well explains the state in a polite
 * live region, and Undo / Clear are real buttons (PAD-02, PAD-07).
 *
 * Locked state (D-14): `disabled` calls `off()`, keeps the strokes visible,
 * and dims the well; re-enabling calls `on()`.
 *
 * Font Awesome icons are never toggled with `hidden` / `peer-*` utilities
 * (FA's unlayered display rule beats Tailwind v4 layers).
 */
import { computed, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue';
import { useDevicePixelRatio as useDpr, useResizeObserver } from '@vueuse/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  faEraser,
  faRotateLeft,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import type SignaturePadLib from 'signature_pad';
import {
  assessInk,
  clampPixelRatio,
  fitPointGroups,
  type SignatureInkState,
} from '@sera/core';

interface Props {
  label?: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Your signature',
  disabled: false,
});

const emit = defineEmits<{ 'update:inkState': [state: SignatureInkState] }>();

const PAD_ARIA_LABEL =
  'Signature pad. Draw your signature with your finger, a stylus, or a mouse.';

const HINTS: Record<SignatureInkState, string> = {
  empty: 'Draw your signature in the box above.',
  insufficient: 'Keep going. Draw your full signature to continue.',
  ready: '',
};

const CONTROL_CLASS =
  'inline-flex items-center gap-1 min-h-[44px] sm:min-h-[32px] px-2 rounded-md text-sm font-semibold text-foreground hover:bg-canvas focus-visible:ring-2 focus-visible:ring-primary';

const uid = useId();
const labelId = `${uid}-label`;
const hintId = `${uid}-hint`;

const wellRef = ref<HTMLElement | null>(null);
const canvasRef = ref<HTMLCanvasElement | null>(null);
const inkState = ref<SignatureInkState>('empty');
const announcement = ref('');

const hint = computed(() => HINTS[inkState.value]);
const controlsLocked = computed(
  () => props.disabled || inkState.value === 'empty',
);

// Never reactive: a Proxy around the library instance breaks its internals.
let pad: SignaturePadLib | null = null;
let box = { w: 0, h: 0, ratio: 0 };
let drawing = false;
let pendingResize = false;
let strokeJustEnded = false;
let unmounted = false;

/**
 * Sizes the backing store from the canvas CSS box. Returns true when strokes
 * from an earlier box were replayed (so the ink state should be re-emitted).
 */
function applySize(): boolean {
  const canvas = canvasRef.value;
  if (!canvas || !pad) return false;
  const w = canvas.offsetWidth;
  const h = canvas.offsetHeight;
  if (w === 0 || h === 0) return false; // not laid out yet (closed dialog)
  const ratio = clampPixelRatio(window.devicePixelRatio);
  if (w === box.w && h === box.h && ratio === box.ratio) return false;
  if (drawing) {
    pendingResize = true;
    return false;
  }
  const hadBox = box.w > 0;
  // fitPointGroups copies (toData() is the library's internal array) and
  // scales down only when the box shrank, so no ink is clipped.
  const replay = hadBox
    ? fitPointGroups(pad.toData(), { w: box.w, h: box.h }, { w, h })
    : [];
  canvas.width = Math.round(w * ratio);
  canvas.height = Math.round(h * ratio);
  // Assigning width resets the context transform, so re-apply the scale.
  canvas.getContext('2d')?.scale(ratio, ratio);
  box = { w, h, ratio };
  pad.fromData(replay);
  return hadBox;
}

function resize(): void {
  if (applySize()) emitInk();
}

function onPointerDown(e: PointerEvent): void {
  try {
    canvasRef.value?.setPointerCapture(e.pointerId);
  } catch {
    // Inactive pointer or no capture support: drawing still works.
  }
}

/** PAD-03: recomputed from the live stroke data on every call. */
function assess() {
  return assessInk(pad ? pad.toData() : [], { width: box.w, height: box.h });
}

function emitInk(): void {
  if (!pad) return;
  const { state } = assess();
  inkState.value = state;
  emit('update:inkState', state);
}

function onBeginStroke(): void {
  drawing = true;
  announcement.value = '';
}

function onEndStroke(): void {
  drawing = false;
  strokeJustEnded = true;
  // Cleared after the trailing click of the same gesture has dispatched.
  setTimeout(() => {
    strokeJustEnded = false;
  }, 0);
  if (pendingResize) {
    pendingResize = false;
    applySize();
  }
  emitInk();
}

onMounted(async () => {
  const canvas = canvasRef.value;
  if (!canvas) return;
  canvas.addEventListener('pointerdown', onPointerDown);
  const { default: SignaturePadCtor } = await import('signature_pad');
  // The dialog can close before the chunk arrives.
  if (unmounted) return;
  pad = new SignaturePadCtor(canvas, {
    penColor: 'black',
    backgroundColor: 'rgba(0,0,0,0)',
  });
  pad.addEventListener('beginStroke', onBeginStroke);
  pad.addEventListener('endStroke', onEndStroke);
  applySize();
  if (props.disabled) pad.off();
});

// The well's own box: fixed px height, so only rotation (width) and the
// 480px height breakpoint ever change it.
useResizeObserver(wellRef, () => resize());

const { pixelRatio } = useDpr();
watch(pixelRatio, () => resize());

/**
 * WR-05: the library's off() drops the move/up listeners without ending an
 * active stroke or dispatching endStroke, so `drawing` would stay true and
 * the dialog's close guard would swallow every dismissal. Settle the
 * wrapper's stroke state here instead (no strokeJustEnded: no pointer gesture
 * is finishing, so no trailing click needs guarding).
 */
function settleStrokeAfterOff(): void {
  if (!drawing) return;
  drawing = false;
  if (pendingResize) {
    pendingResize = false;
    applySize();
  }
  emitInk();
}

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) {
      pad?.off();
      settleStrokeAfterOff();
    } else pad?.on();
  },
);

onBeforeUnmount(() => {
  unmounted = true;
  canvasRef.value?.removeEventListener('pointerdown', onPointerDown);
  pad?.off();
  drawing = false;
  pendingResize = false;
  pad = null;
});

function clear(): void {
  if (!pad) return;
  pad.clear();
  emitInk();
  announcement.value = 'Signature cleared.';
}

function undo(): void {
  if (!pad) return;
  pad.fromData(pad.toData().slice(0, -1));
  emitInk();
}

function isEmpty(): boolean {
  return pad?.isEmpty() ?? true;
}

/** PNG only; deliberately takes no type parameter (D-04). */
function toPngDataUrl(): string {
  return pad ? pad.toDataURL('image/png') : '';
}

function inkOk(): boolean {
  return pad ? assess().ok : false;
}

function isStrokeActiveOrJustEnded(): boolean {
  return drawing || strokeJustEnded;
}

defineExpose({
  clear,
  undo,
  isEmpty,
  toPngDataUrl,
  inkOk,
  isStrokeActiveOrJustEnded,
});
</script>

<template>
  <div data-testid="signature-pad" data-dd-privacy="hidden" data-sentry-block>
    <div class="mb-2 flex items-center justify-between gap-2">
      <span :id="labelId" class="text-sm font-semibold text-foreground">{{
        label
      }}</span>
      <div class="flex items-center gap-1">
        <button
          type="button"
          data-testid="signature-pad-undo"
          aria-label="Undo last stroke"
          :disabled="controlsLocked"
          :class="[
            CONTROL_CLASS,
            controlsLocked ? 'cursor-not-allowed opacity-50' : '',
          ]"
          @click="undo"
        >
          <FontAwesomeIcon
            :icon="faRotateLeft"
            class="size-3.5"
            aria-hidden="true"
          />
          Undo
        </button>
        <button
          type="button"
          data-testid="signature-pad-clear"
          aria-label="Clear signature"
          :disabled="controlsLocked"
          :class="[
            CONTROL_CLASS,
            controlsLocked ? 'cursor-not-allowed opacity-50' : '',
          ]"
          @click="clear"
        >
          <FontAwesomeIcon
            :icon="faEraser"
            class="size-3.5"
            aria-hidden="true"
          />
          Clear
        </button>
      </div>
    </div>
    <div
      ref="wellRef"
      class="relative h-[160px] w-full touch-none overflow-hidden rounded-md border border-dashed border-muted bg-field select-none [@media(max-height:480px)]:h-[120px]"
      :class="{ 'cursor-not-allowed opacity-60': disabled }"
    >
      <!-- Overlays sit under the transparent canvas and never reach the export. -->
      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-x-6 bottom-10 border-b border-border-strong"
      />
      <span
        aria-hidden="true"
        class="pointer-events-none absolute bottom-11 left-6 text-xs text-muted"
      >
        <FontAwesomeIcon :icon="faXmark" />
      </span>
      <canvas
        ref="canvasRef"
        data-testid="signature-pad-canvas"
        role="img"
        :aria-label="PAD_ARIA_LABEL"
        :aria-describedby="hintId"
        class="absolute inset-0 size-full dark:invert"
      />
    </div>
    <p
      :id="hintId"
      data-testid="signature-pad-hint"
      aria-live="polite"
      class="mt-2 min-h-5 text-sm text-foreground"
    >
      {{ hint }}
    </p>
    <span class="sr-only" aria-live="polite">{{ announcement }}</span>
  </div>
</template>
